import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { bookingsAPI, reportsAPI, bookingRequestsAPI } from '../utils/api';
import CalendarView from '../components/CalendarView';
import NoShowChargeModal from '../components/NoShowChargeModal';
import RejectBookingModal from '../components/RejectBookingModal';
import ConfirmModal from '../components/ConfirmModal';
import AdminBookingModal from '../components/AdminBookingModal';
import BookingsTable from '../components/BookingsTable';

function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    status: '',
    payment_status: ''
  });
  const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming', 'table', or 'calendar'
  const [noShowBooking, setNoShowBooking] = useState(null);
  const [rejectBooking, setRejectBooking] = useState(null);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    confirmStyle: 'primary',
    onConfirm: null,
    loading: false
  });

  // Admin booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Booking requests state (cancellations/amendments)
  const [bookingRequests, setBookingRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  // Cancellation charge modal state
  const [chargeModal, setChargeModal] = useState({
    isOpen: false,
    request: null,
    chargeAmount: '',
    loading: false
  });

  useEffect(() => {
    fetchData();
    fetchBookingRequests();
  }, [filters]);

  useEffect(() => {
    if (viewMode === 'upcoming') {
      fetchUpcoming();
    }
  }, [viewMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, statsRes, revenueRes] = await Promise.all([
        bookingsAPI.getAll(filters),
        reportsAPI.getBookingsStats(filters),
        reportsAPI.getRevenue(filters)
      ]);

      setBookings(bookingsRes.data);
      setStats(statsRes.data);
      setRevenue(revenueRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcoming = async () => {
    try {
      const response = await bookingsAPI.getUpcoming();
      setUpcomingBookings(response.data);
    } catch (error) {
      console.error('Error fetching upcoming bookings:', error);
    }
  };

  const fetchBookingRequests = async () => {
    setRequestsLoading(true);
    try {
      const response = await bookingRequestsAPI.getAll('pending');
      setBookingRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching booking requests:', error);
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await bookingRequestsAPI.approve(requestId, {});
      toast.success('Request approved successfully');
      fetchBookingRequests();
      fetchData();
      if (viewMode === 'upcoming') fetchUpcoming();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to approve request');
    }
  };

  const handleRejectRequest = (request) => {
    setConfirmModal({
      isOpen: true,
      title: 'Reject Request',
      message: `Are you sure you want to reject this ${request.request_type} request from ${request.customer_name}? The booking will remain active.`,
      confirmText: 'Reject',
      confirmStyle: 'danger',
      loading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, loading: true }));
        try {
          await bookingRequestsAPI.reject(request.id, { adminResponse: 'Your request could not be approved at this time. Please contact us if you have any questions.' });
          toast.success('Request rejected - customer notified');
          setConfirmModal(prev => ({ ...prev, isOpen: false, loading: false }));
          fetchBookingRequests();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Failed to reject request');
          setConfirmModal(prev => ({ ...prev, loading: false }));
        }
      }
    });
  };

  const handleApproveWithCharge = (request) => {
    // Get suggested charge amount (e.g., 50% of service price)
    const suggestedAmount = request.service_price ? (parseFloat(request.service_price) * 0.5).toFixed(2) : '15.00';
    setChargeModal({
      isOpen: true,
      request,
      chargeAmount: suggestedAmount,
      loading: false
    });
  };

  const submitChargeApproval = async () => {
    const { request, chargeAmount } = chargeModal;
    const amount = parseFloat(chargeAmount);

    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid charge amount');
      return;
    }

    setChargeModal(prev => ({ ...prev, loading: true }));
    try {
      await bookingRequestsAPI.approveWithCharge(request.id, { chargeAmount: amount });
      toast.success(`Cancellation approved - £${amount.toFixed(2)} charged to customer`);
      setChargeModal({ isOpen: false, request: null, chargeAmount: '', loading: false });
      fetchBookingRequests();
      fetchData();
      if (viewMode === 'upcoming') fetchUpcoming();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to process charge');
      setChargeModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleMarkAsPaid = (bookingId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Record Cash Payment',
      message: 'Mark this booking as paid with cash?',
      confirmText: 'Mark Paid',
      confirmStyle: 'success',
      loading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, loading: true }));
        try {
          await bookingsAPI.recordCashPayment(bookingId);
          toast.success('Payment recorded successfully!');
          setConfirmModal(prev => ({ ...prev, isOpen: false, loading: false }));
          fetchData();
          if (viewMode === 'upcoming') fetchUpcoming();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Failed to record payment');
          setConfirmModal(prev => ({ ...prev, loading: false }));
        }
      }
    });
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await bookingsAPI.updateStatus(bookingId, newStatus);
      fetchData();
      if (viewMode === 'upcoming') fetchUpcoming();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update status');
    }
  };

  const handleDelete = (bookingId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Booking',
      message: 'Are you sure you want to permanently delete this booking? This cannot be undone.',
      confirmText: 'Delete',
      confirmStyle: 'danger',
      loading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, loading: true }));
        try {
          await bookingsAPI.delete(bookingId);
          toast.success('Booking deleted successfully');
          setConfirmModal(prev => ({ ...prev, isOpen: false, loading: false }));
          fetchData();
          if (viewMode === 'upcoming') fetchUpcoming();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Failed to delete booking');
          setConfirmModal(prev => ({ ...prev, loading: false }));
        }
      }
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleNoShowChargeSuccess = () => {
    fetchData();
    if (viewMode === 'upcoming') fetchUpcoming();
  };

  const handleApproveBooking = async (bookingId) => {
    try {
      await bookingsAPI.approveBooking(bookingId);
      toast.success('Booking approved! Customer has been notified by email.');
      fetchData();
      if (viewMode === 'upcoming') fetchUpcoming();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to approve booking');
    }
  };

  const handleRejectBooking = (booking) => {
    setRejectBooking(booking);
  };

  const handleRejectSuccess = () => {
    toast.success('Booking rejected. Customer has been notified by email.');
    fetchData();
    if (viewMode === 'upcoming') fetchUpcoming();
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ margin: 0 }}>Dashboard</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowBookingModal(true)}
          style={{ whiteSpace: 'nowrap' }}
        >
          + New Booking
        </button>
      </div>

      {/* Stats Cards - Click to filter */}
      <div className="stats-grid">
        <div
          className="stat-card"
          style={{ cursor: 'pointer', border: filters.status === '' && filters.payment_status === '' && !filters.date ? '2px solid #334155' : undefined }}
          onClick={() => {
            setFilters({ date: '', status: '', payment_status: '' });
            setViewMode('table');
          }}
        >
          <h3>Total Bookings</h3>
          <div className="value">{stats?.total_bookings || 0}</div>
          <div className="subtitle" style={{ fontSize: '0.75rem' }}>Click to show all</div>
        </div>
        <div
          className="stat-card"
          style={{ cursor: 'pointer', border: filters.status === 'confirmed' ? '2px solid #10B981' : undefined }}
          onClick={() => {
            setFilters({ ...filters, status: filters.status === 'confirmed' ? '' : 'confirmed', date: '' });
            setViewMode('table');
          }}
        >
          <h3>Confirmed</h3>
          <div className="value" style={{ color: '#10B981' }}>{stats?.confirmed || 0}</div>
          <div className="subtitle" style={{ fontSize: '0.75rem' }}>Click to filter</div>
        </div>
        <div
          className="stat-card"
          style={{ cursor: 'pointer', border: filters.status === 'pending' ? '2px solid #F59E0B' : undefined }}
          onClick={() => {
            setFilters({ ...filters, status: filters.status === 'pending' ? '' : 'pending', date: '' });
            setViewMode('table');
          }}
        >
          <h3>Pending</h3>
          <div className="value" style={{ color: '#F59E0B' }}>{stats?.pending || 0}</div>
          <div className="subtitle" style={{ fontSize: '0.75rem' }}>Click to filter</div>
        </div>
        <div
          className="stat-card"
          style={{ cursor: 'pointer', border: filters.payment_status === 'pending' ? '2px solid #EF4444' : undefined }}
          onClick={() => {
            setFilters({ ...filters, payment_status: filters.payment_status === 'pending' ? '' : 'pending', date: '' });
            setViewMode('table');
          }}
        >
          <h3>Unpaid Bookings</h3>
          <div className="value" style={{ color: '#EF4444' }}>
            {bookings.filter(b => b.payment_status === 'pending' || !b.payment_status).length}
          </div>
          <div className="subtitle" style={{ fontSize: '0.75rem' }}>Click to filter</div>
        </div>
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <div className="value" style={{ color: '#334155' }}>
            £{revenue?.total_revenue?.toFixed(2) || '0.00'}
          </div>
          <div className="subtitle">
            Card: £{revenue?.card_revenue?.toFixed(2) || '0.00'} |
            Cash: £{revenue?.cash_revenue?.toFixed(2) || '0.00'}
          </div>
        </div>
      </div>

      {/* Pending Booking Requests */}
      {bookingRequests.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #F59E0B' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ background: '#FEF3C7', color: '#92400E', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.875rem' }}>
                {bookingRequests.length}
              </span>
              Customer Requests Pending
            </h2>
          </div>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            Customers are waiting for your response on these cancellation/amendment requests.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {bookingRequests.map(request => (
              <div
                key={request.id}
                style={{
                  background: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  borderRadius: '8px',
                  padding: '1rem'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{
                        background: request.request_type === 'cancellation' ? '#FEE2E2' : '#DBEAFE',
                        color: request.request_type === 'cancellation' ? '#991B1B' : '#1E40AF',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}>
                        {request.request_type}
                      </span>
                      {request.hours_notice < 24 && (
                        <span style={{
                          background: '#FEE2E2',
                          color: '#991B1B',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}>
                          Late Notice ({request.hours_notice}h)
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '0 0 0.25rem 0', fontWeight: 600 }}>
                      {request.customer_name} - {request.service_name}
                    </p>
                    <p style={{ margin: '0 0 0.25rem 0', color: '#666', fontSize: '0.875rem' }}>
                      Original: {request.booking_date} at {request.start_time}
                    </p>
                    {request.request_type === 'amendment' && request.requested_date && (
                      <p style={{ margin: '0 0 0.25rem 0', color: '#1E40AF', fontSize: '0.875rem' }}>
                        Requested: {request.requested_date} at {request.requested_time}
                      </p>
                    )}
                    {request.reason && (
                      <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.875rem', fontStyle: 'italic' }}>
                        "{request.reason}"
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-success"
                      style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                      onClick={() => handleApproveRequest(request.id)}
                    >
                      Approve
                    </button>
                    {/* Show "Approve with Charge" only for cancellation requests */}
                    {request.request_type === 'cancellation' && (
                      <button
                        className="btn"
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#E09F3E',
                          color: 'white',
                          border: 'none'
                        }}
                        onClick={() => handleApproveWithCharge(request)}
                        title="Cancel booking and charge a fee"
                      >
                        Approve + Charge
                      </button>
                    )}
                    <button
                      className="btn btn-danger"
                      style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                      onClick={() => handleRejectRequest(request)}
                      title={request.request_type === 'cancellation' ? 'Reject - booking remains active' : 'Reject change request'}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Toggle & Bookings */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h2 style={{ margin: 0 }}>Bookings</h2>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              className={`btn ${viewMode === 'upcoming' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('upcoming')}
            >
              Upcoming
            </button>
            <button
              className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('table')}
            >
              By Date
            </button>
            <button
              className={`btn ${viewMode === 'calendar' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('calendar')}
            >
              Calendar
            </button>
          </div>
        </div>

        {viewMode === 'upcoming' ? (
          <div>
            <p style={{ color: '#666', marginBottom: '1rem' }}>Showing upcoming appointments from today onwards</p>
            <BookingsTable
              bookings={upcomingBookings}
              onStatusChange={handleStatusChange}
              onMarkPaid={handleMarkAsPaid}
              onDelete={handleDelete}
              onNoShowCharge={setNoShowBooking}
              onApprove={handleApproveBooking}
              onReject={handleRejectBooking}
            />
          </div>
        ) : viewMode === 'calendar' ? (
          <CalendarView
            onMarkAsPaid={handleMarkAsPaid}
            onStatusChange={handleStatusChange}
          />
        ) : (
          <>
            <div className="filters" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  name="date"
                  value={filters.date}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="form-group">
                <label>Booking Status</label>
                <select name="status" value={filters.status} onChange={handleFilterChange}>
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label>Payment Status</label>
                <select name="payment_status" value={filters.payment_status} onChange={handleFilterChange}>
                  <option value="">All</option>
                  <option value="pending">Unpaid</option>
                  <option value="completed">Paid</option>
                </select>
              </div>
              <button
                className="btn btn-secondary"
                style={{ height: 'fit-content' }}
                onClick={() => setFilters({ date: '', status: '', payment_status: '' })}
              >
                Clear Filters
              </button>
            </div>

            {/* Bookings Table */}
            <BookingsTable
              bookings={bookings}
              onStatusChange={handleStatusChange}
              onMarkPaid={handleMarkAsPaid}
              onDelete={handleDelete}
              onNoShowCharge={setNoShowBooking}
              onApprove={handleApproveBooking}
              onReject={handleRejectBooking}
            />
          </>
        )}
      </div>

      {/* No-Show Charge Modal */}
      <NoShowChargeModal
        booking={noShowBooking}
        isOpen={!!noShowBooking}
        onClose={() => setNoShowBooking(null)}
        onSuccess={handleNoShowChargeSuccess}
      />

      {/* Reject Booking Modal */}
      <RejectBookingModal
        booking={rejectBooking}
        isOpen={!!rejectBooking}
        onClose={() => setRejectBooking(null)}
        onSuccess={handleRejectSuccess}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmStyle={confirmModal.confirmStyle}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirmModal}
        loading={confirmModal.loading}
      />

      {/* Admin Booking Modal */}
      <AdminBookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        onSuccess={() => {
          fetchData();
          if (viewMode === 'upcoming') fetchUpcoming();
        }}
      />

      {/* Cancellation Charge Modal */}
      {chargeModal.isOpen && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#8B2635' }}>
              Approve with Cancellation Fee
            </h3>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Cancel the booking and charge a fee to {chargeModal.request?.customer_name}'s card on file.
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 500 }}>
                Service: {chargeModal.request?.service_name}
              </p>
              <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '0.875rem' }}>
                Original price: £{chargeModal.request?.service_price}
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Cancellation Fee (£)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={chargeModal.chargeAmount}
                onChange={(e) => setChargeModal(prev => ({ ...prev, chargeAmount: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '1rem'
                }}
                placeholder="e.g. 15.00"
              />
              <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.75rem' }}>
                Suggested: 50% of service price for late cancellations
              </p>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setChargeModal({ isOpen: false, request: null, chargeAmount: '', loading: false })}
                disabled={chargeModal.loading}
                style={{ minHeight: '44px' }}
              >
                Cancel
              </button>
              <button
                className="btn"
                style={{ backgroundColor: '#E09F3E', color: 'white', border: 'none', minHeight: '44px' }}
                onClick={submitChargeApproval}
                disabled={chargeModal.loading}
              >
                {chargeModal.loading ? 'Processing...' : `Charge £${chargeModal.chargeAmount || '0.00'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
